import { createAction } from 'redux-actions'
import validateEvent from '@segment/loosely-validate-event'
import SSE from '@segment/sse.js'
import { serialize } from 'query-data'
import {
  getActiveWorkspaceSlug,
  getSourceSettingWriteKey,
  getSimulatorIntegration,
  getSimulatorMethod,
  getSimulatorPayload,
  getSimulatorWriteKey,
  getActiveWorkspace,
  getSimulatorMetadataSlugs
} from 'selectors'
import * as analytics from '../analytics'

export const SET_SIMULATOR_SOURCE = 'SET_SIMULATOR_SOURCE'
export const setSimulatorSource = sourceSlug => (dispatch, getState) => {
  const state = getState()
  const workspaceSlug = getActiveWorkspaceSlug(state)
  const writeKey = getSourceSettingWriteKey(state, workspaceSlug, sourceSlug) || ''
  dispatch({
    type: SET_SIMULATOR_SOURCE,
    payload: {
      sourceSlug,
      writeKey
    }
  })
}

export const SET_SIMULATOR_INTEGRATION = 'SET_SIMULATOR_INTEGRATION'
export const setSimulatorIntegration = createAction(SET_SIMULATOR_INTEGRATION)

export const SET_SIMULATOR_API_METHOD = 'SET_SIMULATOR_API_METHOD'
export const setSimulatorAPIMethod = createAction(SET_SIMULATOR_API_METHOD)

export const SET_SIMULATOR_PAYLOAD = 'SET_SIMULATOR_PAYLOAD'

export const SET_SIMULATOR_PAYLOAD_ERROR = 'SET_SIMULATOR_PAYLOAD_ERROR'
const setSimulatorPayloadError = createAction(SET_SIMULATOR_PAYLOAD_ERROR)
export const setSimulatorPayload = payload => (dispatch, getState) => {
  const state = getState()
  const method = getSimulatorMethod(state) || 'track'

  dispatch({ type: SET_SIMULATOR_PAYLOAD, payload })

  try {
    const event = JSON.parse(payload)
    validateEvent(event, method)
    dispatch(setSimulatorPayloadError(''))
  } catch (err) {
    dispatch(setSimulatorPayloadError(err.message))
  }
}

export const SET_SIMULATOR_ACTIVE_TAB = 'SET_SIMULATOR_ACTIVE_TAB'
export const setSimulatorActiveTab = (activeTab, workspaceSlug) => (dispatch, getState) => {
  dispatch({ type: SET_SIMULATOR_ACTIVE_TAB, payload: activeTab })
  const state = getState()
  const workspace = getActiveWorkspace(state)
  analytics.simulatorTabClicked(workspace, activeTab)
}

export const SIMULATE_EVENT_RESET = 'SIMULATE_EVENT_RESET'
export const simulateEventReset = createAction(SIMULATE_EVENT_RESET)

export const SIMULATE_EVENT_REQUEST = 'SIMULATE_EVENT_REQUEST'
const simulateEventRequest = createAction(SIMULATE_EVENT_REQUEST)

export const SIMULATE_EVENT_ERROR = 'SIMULATE_EVENT_ERROR'
const simulateEventError = createAction(SIMULATE_EVENT_ERROR)

export const SIMULATE_EVENT_RESPONSE = 'SIMULATE_EVENT_RESPONSE'
const simulateEventResponse = createAction(SIMULATE_EVENT_RESPONSE)

export const trackSendEvent = () => (dispatch, getState) => {
  const state = getState()
  const workspace = getActiveWorkspace(state)
  const method = getSimulatorMethod(state)
  const { sourceMetadataSlug, integrationMetadataSlug } = getSimulatorMetadataSlugs(state)

  analytics.simulatorSendEventClicked(workspace, method, sourceMetadataSlug, integrationMetadataSlug)
}

export const sendSimulatorEvent = () => (dispatch, getState) => {
  dispatch(simulateEventRequest())
  const state = getState()
  const workspaceSlug = getActiveWorkspaceSlug(state)
  const workspace = getActiveWorkspace(state)
  const method = getSimulatorMethod(state)
  const payload = getSimulatorPayload(state)
  const writeKey = getSimulatorWriteKey(state)
  const integration = getSimulatorIntegration(state)
  const { sourceMetadataSlug, integrationMetadataSlug } = getSimulatorMetadataSlugs(state)

  // encode our query params
  const params = serialize({
    write_key: writeKey,
    payload: JSON.parse(payload),
    method,
    integration,
    sourceMetadataSlug,
    integrationMetadataSlug
  })

  // the simulator API uses SSE to send down data
  const sse = new SSE(`/api/workspaces/${workspaceSlug}/simulator/stream?params=${params}`, {
    withCredentials: true
  })

  sse.on('error', error => {
    sse.close()
    dispatch(simulateEventError({ error }))
    analytics.simulatorEventResponse(workspace, 'failure', method, sourceMetadataSlug, integrationMetadataSlug)
  })

  sse.on('timeout', () => {
    // TODO: we should treat timeouts differently than errors in the UI
    sse.close()
  })

  sse.on('response', body => {
    sse.close()
    dispatch(simulateEventResponse(body))
    analytics.simulatorEventResponse(workspace, 'success', method, sourceMetadataSlug, integrationMetadataSlug)
  })
}

export const SHOW_SIMULATOR_ACKNOWLEDGE_MODAL = 'SHOW_SIMULATOR_ACKNOWLEDGE_MODAL'
export const showSimulatorAcknowledgeModal = () => (dispatch, getState) => {
  dispatch({ type: SHOW_SIMULATOR_ACKNOWLEDGE_MODAL })
  const state = getState()
  const workspace = getActiveWorkspace(state)
  analytics.simulatorModalShown(workspace, 'Simulator Beta Notice')
}

export const SHOW_SIMULATOR_SEND_CONFIRM_MODAL = 'SHOW_SIMULATOR_SEND_CONFIRM_MODAL'
export const showSimulatorSendConfirmModal = () => (dispatch, getState) => {
  dispatch({ type: SHOW_SIMULATOR_SEND_CONFIRM_MODAL })
  const state = getState()
  const workspace = getActiveWorkspace(state)
  analytics.simulatorModalShown(workspace, 'Simulator Send Event Confirmation')
}

export const HIDE_SIMULATOR_MODALS = 'HIDE_SIMULATOR_MODALS'
export const hideSimulatorModals = (didAcknowledge, modalName) => (dispatch, getState) => {
  dispatch({ type: HIDE_SIMULATOR_MODALS })
  const state = getState()
  const workspace = getActiveWorkspace(state)
  analytics.simulatorModalClosed(workspace, didAcknowledge, modalName)
}

export const SHOW_SIMULATOR_SOURCE_REQUIRED_ERROR = 'SHOW_SIMULATOR_SOURCE_REQUIRED_ERROR'
export const showSimulatorSourceRequriedError = createAction(SHOW_SIMULATOR_SOURCE_REQUIRED_ERROR)



// WEBPACK FOOTER //
// ./actions/simulator.js